/* Independent collection sections; product data and pricing stay in Liquid. */
if (!customElements.get('iah-motion-collection')) {
  customElements.define('iah-motion-collection', class extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.items = Array.from(this.querySelectorAll('[data-product]'));
      if (!this.items.length) return;
      this.controller = new AbortController();
      this.animations = new Map();
      this.stage = this.querySelector('.iah-motion__stage');
      this.visuals = this.items.map((item) => item.querySelector('[data-visual]'));
      this.copies = this.items.map((item) => item.querySelector('[data-copy]'));
      this.reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.duration = Math.min(1400, Math.max(400, Number(this.dataset.duration) || 900));
      this.current = 0;
      this.entered = false;
      this.seen = new Set();
      this.listen(this.reduced, 'change', () => {
        if (this.reduced.matches) this.cancelAnimations();
      });
      if (this.dataset.layout === 'offscreen') this.setupOffscreen();
      else this.setupCross();
      this.dataset.enhanced = '';
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.observer?.disconnect();
      this.resizeObserver?.disconnect();
      cancelAnimationFrame(this.resizeFrame);
      this.cancelAnimations();
      this.controller = null;
      this.gesture = null;
      this.lastWidth = null;
      delete this.dataset.enhanced;
      this.visuals?.forEach((visual) => {
        visual.removeAttribute('style');
        visual.removeAttribute('tabindex');
        visual.removeAttribute('aria-hidden');
      });
      this.copies?.forEach((copy) => { copy.hidden = false; });
      this.querySelectorAll('[data-navigation]').forEach((control) => { control.hidden = true; });
    }

    listen(target, event, callback, options = {}) {
      target.addEventListener(event, callback, { ...options, signal: this.controller.signal });
    }

    get motionEnabled() { return this.dataset.motion === 'true' && !this.reduced.matches; }

    cancelAnimations() {
      this.animations?.forEach((animation) => animation.cancel());
      this.animations?.clear();
    }

    animate(element, frames, delay = 0) {
      this.animations.get(element)?.cancel();
      if (!this.motionEnabled || !element.animate) return;
      const animation = element.animate(frames, {
        duration: this.duration, delay, easing: 'cubic-bezier(.16, 1, .3, 1)', fill: 'backwards'
      });
      this.animations.set(element, animation);
      animation.onfinish = () => {
        if (this.animations.get(element) === animation) this.animations.delete(element);
      };
    }

    setupOffscreen() {
      this.querySelector('[data-navigation]').hidden = this.items.length < 2;
      // Set the enhancement before measuring: the no-JS fallback uses a grid.
      this.dataset.enhanced = '';
      this.place(false);
      this.listen(this, 'click', (event) => {
        if (Date.now() < (this.suppressClickUntil || 0)) {
          event.preventDefault();
          return;
        }
        const step = event.target.closest('[data-step]');
        const visual = event.target.closest('[data-visual]');
        if (step) this.select(this.current + Number(step.dataset.step));
        else if (visual && this.visuals.indexOf(visual) !== this.current) {
          event.preventDefault();
          this.select(this.visuals.indexOf(visual));
        }
      });
      this.listen(this, 'keydown', (event) => {
        if (event.altKey || event.ctrlKey || event.metaKey || this.items.length < 2) return;
        if (!event.target.closest('[data-visual], [data-navigation]')) return;
        const directions = { ArrowLeft: this.current - 1, ArrowRight: this.current + 1, Home: 0, End: this.items.length - 1 };
        if (Object.hasOwn(directions, event.key)) {
          event.preventDefault();
          this.select(directions[event.key]);
        }
      });
      this.listen(this.stage, 'dragstart', (event) => event.preventDefault());
      this.setupTouch();
      this.listen(this.stage, 'pointerdown', (event) => {
        // Touch is handled by setupTouch: iOS cancels pointer streams on links.
        if (event.pointerType === 'touch' || !event.isPrimary || event.button !== 0 || this.items.length < 2 || !event.target.closest('[data-visual]')) return;
        this.gesture = { id: event.pointerId, x: event.clientX, y: event.clientY, captured: false };
      });
      this.listen(this.stage, 'pointermove', (event) => {
        const gesture = this.gesture;
        if (!gesture || gesture.id !== event.pointerId) return;
        const dx = event.clientX - gesture.x;
        const dy = event.clientY - gesture.y;
        if (!gesture.captured && Math.abs(dy) > 14 && Math.abs(dy) > Math.abs(dx)) {
          this.gesture = null;
          return;
        }
        if (!gesture.captured && Math.abs(dx) > 14 && Math.abs(dx) > Math.abs(dy)) {
          this.stage.setPointerCapture(event.pointerId);
          gesture.captured = true;
        }
      });
      this.listen(this.stage, 'pointerup', (event) => {
        const gesture = this.gesture;
        if (!gesture || gesture.id !== event.pointerId) return;
        this.gesture = null;
        if (gesture.captured) {
          this.suppressClickUntil = Date.now() + 350;
          if (this.stage.hasPointerCapture(event.pointerId)) this.stage.releasePointerCapture(event.pointerId);
          const dx = event.clientX - gesture.x;
          if (Math.abs(dx) > 45) this.select(this.current + (dx < 0 ? 1 : -1));
        }
      });
      this.listen(this.stage, 'pointercancel', () => { this.gesture = null; });
      this.listen(this.stage, 'lostpointercapture', () => { this.gesture = null; });
      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(() => {
          const width = this.stage.clientWidth;
          if (width === this.lastWidth) return;
          this.lastWidth = width;
          cancelAnimationFrame(this.resizeFrame);
          this.resizeFrame = requestAnimationFrame(() => this.place(false));
        });
        this.resizeObserver.observe(this.stage);
      } else this.listen(window, 'resize', () => this.place(false));
      if ('IntersectionObserver' in window) {
        this.observer = new IntersectionObserver((entries) => {
          if (!entries.some((entry) => entry.isIntersecting)) return;
          this.entered = true;
          this.place(true, true);
          this.observer.disconnect();
        }, { threshold: .15 });
        this.observer.observe(this.stage);
      }
    }

    setupTouch() {
      // Swipe anywhere on the stage; vertical drags keep scrolling the page.
      let touch = null;
      this.listen(this.stage, 'touchstart', (event) => {
        touch = null;
        if (event.touches.length !== 1 || this.items.length < 2 || event.target.closest('[data-navigation]')) return;
        const point = event.touches[0];
        touch = { x: point.clientX, y: point.clientY, axis: null };
      }, { passive: true });
      this.listen(this.stage, 'touchmove', (event) => {
        if (!touch) return;
        if (event.touches.length !== 1) {
          touch = null;
          return;
        }
        const point = event.touches[0];
        const dx = point.clientX - touch.x;
        const dy = point.clientY - touch.y;
        if (!touch.axis && Math.max(Math.abs(dx), Math.abs(dy)) > 10) touch.axis = Math.abs(dx) > Math.abs(dy) ? 'x' : 'y';
        if (touch.axis === 'y') touch = null;
        else if (touch.axis === 'x' && event.cancelable) event.preventDefault();
      }, { passive: false });
      this.listen(this.stage, 'touchend', (event) => {
        const start = touch;
        touch = null;
        if (!start || start.axis !== 'x') return;
        this.suppressClickUntil = Date.now() + 350;
        const dx = event.changedTouches[0].clientX - start.x;
        if (Math.abs(dx) > 40) this.select(this.current + (dx < 0 ? 1 : -1));
      });
      this.listen(this.stage, 'touchcancel', () => { touch = null; });
    }

    select(index) {
      const next = (index + this.items.length) % this.items.length;
      if (next === this.current) return;
      const active = document.activeElement;
      const moveFocus = this.items[this.current].contains(active);
      // Focus a stable control before concealing the old product.
      if (moveFocus) this.querySelector('[data-step="1"]').focus({ preventScroll: true });
      this.current = next;
      this.entered = true;
      this.observer?.disconnect();
      this.place(true);
      this.querySelector('[data-status]').textContent = `${next + 1} / ${this.items.length}: ${this.copies[next].querySelector('.iah-motion__title').innerText.trim()}. ${this.copies[next].querySelector('.price').innerText.trim()}`;
    }

    place(moving, entrance = false) {
      const width = this.stage.clientWidth;
      const stride = width * (width < 750 ? .74 : .43);
      this.visuals.forEach((visual, index) => {
        let offset = index - this.current;
        if (this.items.length > 2) {
          if (offset > this.items.length / 2) offset -= this.items.length;
          if (offset < -this.items.length / 2) offset += this.items.length;
        }
        const visible = Math.abs(offset) <= 1;
        const rotation = offset === 0 ? -7 : (offset < 0 ? 12 : 9);
        const y = offset === 0 ? 0 : (offset < 0 ? 24 : -18);
        const transform = `translate(calc(-50% + ${offset * stride}px), ${y}px) rotate(${rotation}deg)`;
        const from = getComputedStyle(visual).transform;
        this.animations.get(visual)?.cancel();
        visual.style.transform = transform;
        visual.style.zIndex = offset === 0 ? '3' : '2';
        visual.style.visibility = visible ? 'visible' : 'hidden';
        visual.tabIndex = offset === 0 ? 0 : -1;
        visual.setAttribute('aria-hidden', String(offset !== 0));
        this.copies[index].hidden = offset !== 0;
        if (moving && visible) {
          const side = offset < 0 ? -1 : 1;
          this.animate(visual, [
            { transform: entrance ? `translate(calc(-50% + ${side * (width + 400)}px), 120px) rotate(${side * 35}deg)` : from },
            { transform }
          ], entrance ? index * 60 : 0);
        }
      });
      this.querySelector('[data-current]').textContent = String(this.current + 1).padStart(2, '0');
    }

    setupCross() {
      if (!('IntersectionObserver' in window)) return;
      this.observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          const index = this.items.indexOf(entry.target);
          this.enterRow(index);
          this.observer.unobserve(entry.target);
        });
      }, { threshold: .12 });
      this.items.forEach((item) => this.observer.observe(item));
      // Keyboard users should never chase a link as it moves into view.
      this.listen(this, 'focusin', (event) => {
        const item = event.target.closest('[data-product]');
        const index = this.items.indexOf(item);
        if (index < 0) return;
        this.seen.add(index);
        this.observer.unobserve(item);
        [this.visuals[index], this.copies[index]].forEach((element) => {
          this.animations.get(element)?.cancel();
          this.animations.delete(element);
        });
      });
    }

    enterRow(index) {
      if (this.seen.has(index)) return;
      this.seen.add(index);
      const side = index % 2 === 0 ? -1 : 1;
      const width = this.stage.clientWidth;
      this.animate(this.visuals[index], [
        { transform: `translateX(${side * width}px) rotate(${side * 18}deg)` },
        { transform: 'none' }
      ]);
      this.animate(this.copies[index], [
        { transform: `translateX(${-side * Math.min(width * .3, 300)}px)` },
        { transform: 'none' }
      ], 100);
    }
  });
}
